import { Component, Inject, TemplateRef, ViewChild } from '@angular/core'
import { MapService } from 'src/app/core/services/map.service'
import { Subject, debounceTime, interval, takeUntil } from 'rxjs'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { Layers } from 'src/app/shared/layers-config/layers'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { getMetricFromMap } from '../enums/simulation-metrics.enum'
import { DialogCloseEventWithResult, ModalService } from '@inst-iot/bosch-angular-ui-components'

import { LayerSimulationWarningService } from 'src/app/shared/layers/layer-simulation-warning.service'
import { SimulationEditorLayerService } from 'src/app/shared/layers/layer-simulation-editor.service'

import { DataSimulationWarningService } from 'src/app/shared/services/data-simulation-warning.service'
import { AppConfigModel } from 'src/app/core/models'
import { APP_CONFIG } from 'src/app/app.config'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { AuthService } from 'src/app/core/services/auth.service'
import { WarningDatasources } from 'src/app/shared/enums/warning-types.enum'

@Component({
  selector: 'app-simulation-page',
  templateUrl: './simulation-page.component.html',
  styleUrls: ['./simulation-page.component.scss'],
  standalone: false,
  providers: [
    LayerSimulationWarningService,
    SimulationEditorLayerService,
    DataSimulationWarningService,
    { provide: 'metrics', useValue: getMetricFromMap },
  ],
})
export class SimulationPageComponent {
  private stopTimer = new Subject<void>()
  private readonly editorIntroStorageKey = 'simulation-editor-intro-hidden'
  private readonly editorFeatureHighlightStorageKey = 'simulation-editor-feature-highlight-seen'

  @ViewChild('editorIntroDialog') editorIntroDialog!: TemplateRef<any>

  public isInLiveMode = false
  public autoRefreshIntervalInMs = 0
  public displayRefreshInterval = 0
  public layerIds = Layers
  public currentTimestamp: Date | undefined
  public currentTimespan: number | undefined
  public warningId = ''
  public Permissions = Permissions
  public isRoadWarningCreationVisible: boolean = false
  public isHexagonWarningCreationVisible: boolean = false
  public isSimulationEditorVisible: boolean = false
  public onDestroy: boolean = false
  public allowedFilters: WarningDatasources[] = []
  public currentFilter: string = ''
  public isLoading: boolean = false
  public visibleSimulationsCount: number = 0
  public isHistoryVisible = false
  public editorIntroDontShowAgain = false
  public showEditorMenuHighlight = false

  constructor(
    @Inject(APP_CONFIG) public readonly config: AppConfigModel,
    private mapService: MapService,
    public appState: AppStateService,
    public AuthService: AuthService,
    private simulationWarningLayerService: LayerSimulationWarningService,
    private simulationEditorLayerService: SimulationEditorLayerService,
    private readonly modalService: ModalService,
  ) {
    this.allowedFilters = this.AuthService.getUserClaims().userDataFilters
    this.currentFilter = this.allowedFilters[0]
    this.mapService.activateGeoLocationSearch()
    this.mapService.setPosition(
      this.appState.getState().lat,
      this.appState.getState().lon,
      this.appState.getState().zoom,
    )

    this.updateQueryParameters()

    this.mapService.mapViewChanged
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.updateQueryParameters()
      })

    if (this.appState.getState().historyEnabled) {
      this.isHistoryVisible = true
    }

    this.evaluateQueryParameters()
    this.showEditorMenuHighlight = !this.isEditorFeatureHighlightSeen()
    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {
      this.evaluateQueryParameters()
    })
  }

  ngOnDestroy() {
    this.mapService.deactivateGeoLocationSearch()
    this.stopTimer.next()
    this.stopTimer.complete()
  }

  setLiveModeRefreshInterval(seconds: number) {
    this.autoRefreshIntervalInMs = seconds * 1000
    this.displayRefreshInterval = seconds
    this.stopLiveMode()
    if (this.autoRefreshIntervalInMs > 0) {
      this.startLiveMode()
    }
  }

  startLiveMode() {
    this.isInLiveMode = true
    this.stopTimer = new Subject<void>()
    interval(this.autoRefreshIntervalInMs)
      .pipe(takeUntil(this.stopTimer))
      .subscribe(() => {
        this.simulationWarningLayerService.reload()
      })
  }

  stopLiveMode() {
    this.isInLiveMode = false
    this.stopTimer.next()
  }

  setCurrentFilter(filter: WarningDatasources) {
    this.currentFilter = filter
    this.appState.hasWarningDatasourceChanged$.next(filter)
  }

  evaluateQueryParameters() {
    if (this.appState.getState().layers?.includes(Layers.CreateSimAreaWarnings.name)) {
      this.isHexagonWarningCreationVisible = true
    }
    if (this.appState.getState().layers?.includes(Layers.CreateSimRoadWarnings.name)) {
      this.isRoadWarningCreationVisible = true
    }
  }

  updateQueryParameters() {
    const layers = this.appState.selectLayers()
    if (!layers.some((layer) => layer === Layers.SimWarning.name)) {
      layers.push(Layers.SimWarning.name)
    }

    this.appState.setState(AppPaths.Simulation, {
      lat: this.mapService.map.getCenter().lat,
      lon: this.mapService.map.getCenter().lng,
      zoom: this.mapService.map.getZoom(),
      layers: layers.toString(),
    })
  }

  async enableWarningImportExport() {
    if (!this.isEditorFeatureHighlightSeen()) {
      this.rememberEditorFeatureHighlightSeen()
      this.showEditorMenuHighlight = false
    }

    const shouldOpenEditor = !this.isSimulationEditorVisible

    this.isSimulationEditorVisible = !this.isSimulationEditorVisible

    const layers = this.appState
      .selectLayers()
      .filter(
        (layer) =>
          layer !== Layers.CreateSimAreaWarnings.name &&
          layer !== Layers.CreateSimRoadWarnings.name,
      )

    if (this.isSimulationEditorVisible) {
      this.isHexagonWarningCreationVisible = true
      this.isRoadWarningCreationVisible = true
      layers.push(Layers.CreateSimAreaWarnings.name)
      layers.push(Layers.CreateSimRoadWarnings.name)

      this.appState.setState(AppPaths.Simulation, {
        simulationEditor: true,
        layers: layers.toString(),
      })
      if (shouldOpenEditor && !this.isEditorIntroDismissed()) {
        this.editorIntroDontShowAgain = false
        this.isSimulationEditorVisible = true

        await this.openEditorIntroDialog()
      }
      return
    }

    this.isHexagonWarningCreationVisible = false
    this.isRoadWarningCreationVisible = false

    this.appState.setState(AppPaths.Simulation, {
      simulationEditor: false,
      layers: layers.toString(),
    })
  }

  public get shouldShowEditorIntroCheckbox(): boolean {
    return !this.isEditorIntroDismissed()
  }

  private isEditorIntroDismissed(): boolean {
    try {
      return localStorage.getItem(this.editorIntroStorageKey) === 'true'
    } catch {
      return false
    }
  }

  private rememberEditorIntroChoice(): void {
    try {
      localStorage.setItem(
        this.editorIntroStorageKey,
        this.editorIntroDontShowAgain ? 'true' : 'false',
      )
    } catch {
      // ignore storage failures
    }
  }

  private isEditorFeatureHighlightSeen(): boolean {
    try {
      return localStorage.getItem(this.editorFeatureHighlightStorageKey) === 'true'
    } catch {
      return false
    }
  }

  private rememberEditorFeatureHighlightSeen(): void {
    try {
      localStorage.setItem(this.editorFeatureHighlightStorageKey, 'true')
    } catch {
      // ignore storage failures
    }
  }

  async openEditorHelpDialog(): Promise<void> {
    this.editorIntroDontShowAgain = false
    await this.openEditorIntroDialog()
  }

  private async openEditorIntroDialog(): Promise<void> {
    const closeEventResult = await this.modalService.open<DialogCloseEventWithResult>(
      this.editorIntroDialog,
      {
        maxWidth: '700px',
        position: 'middle',
      },
    )

    if (closeEventResult?.event) {
      this.rememberEditorIntroChoice()
    }
  }

  enableSetHexagonWarningMode() {
    this.isHexagonWarningCreationVisible = !this.isHexagonWarningCreationVisible
    const layers = this.appState
      .selectLayers()
      .filter((layer) => !layer.includes(Layers.CreateSimAreaWarnings.name))

    if (this.isHexagonWarningCreationVisible) {
      layers.push(Layers.CreateSimAreaWarnings.name)
    }

    this.appState.setState(AppPaths.Simulation, {
      layers: layers?.toString(),
    })
  }
  enableSetRoadWarningMode() {
    this.isRoadWarningCreationVisible = !this.isRoadWarningCreationVisible
    const layers = this.appState
      .selectLayers()
      .filter((layer) => !layer.includes(Layers.CreateSimRoadWarnings.name))

    if (this.isRoadWarningCreationVisible) {
      layers.push(Layers.CreateSimRoadWarnings.name)
    }

    this.appState.setState(AppPaths.Simulation, {
      layers: layers?.toString(),
    })
  }

  closeImportExport() {
    this.isSimulationEditorVisible = false
    this.isHexagonWarningCreationVisible = false
    this.isRoadWarningCreationVisible = false

    const layers = this.appState
      .selectLayers()
      .filter(
        (layer) =>
          !layer.includes(Layers.CreateSimAreaWarnings.name) &&
          !layer.includes(Layers.CreateSimRoadWarnings.name),
      )

    this.appState.setState(AppPaths.Simulation, {
      simulationEditor: false,
      layers: layers.toString(),
    })
  }
}
